import omit from 'lodash/omit';
import { DeepRequired } from 'ts-essentials';

import {
  isAppleDevice,
  KeyboardKeyCombination,
  normalizeKeyCombinations,
  normativeOrderKeyCombinations,
} from 'utils/keyboard';

import {
  CommandBase,
  CommandDefinitionBase,
  CommandGroup,
} from '../types/command.types';
import { commandGroupDelimiter } from '../utils/commandUtils';

type KeybindingsKey = keyof Pick<
  DeepRequired<CommandBase>['keyboard'],
  'keybindings_mac' | 'keybindings_pc' | 'keybindings'
>;

const osKeybindingsKey: KeybindingsKey = isAppleDevice
  ? 'keybindings_mac'
  : 'keybindings_pc';

/**
 * Creates a command definition.
 *
 * @param command The command to create.
 * @remark This function should not be used at runtime as what it produces is
 * required to be static.
 */
export function createCommand<
  TGroup extends CommandGroup,
  TName extends string,
  TCommand extends CommandBase<TName, TGroup>,
  TCommandDefinition extends CommandDefinitionBase<TCommand>,
>(command: TCommand): TCommandDefinition {
  const name = `${command.group}${commandGroupDelimiter}${command.name}`;
  const keybindings = (command.keyboard?.[osKeybindingsKey] ??
    command.keyboard?.keybindings) as
    | readonly KeyboardKeyCombination[]
    | undefined;

  return {
    group: command.group,
    name,
    label: command.label,
    description: command.description,
    featureFlag: command.featureFlag,
    useRegisterHandler: command.useRegisterHandler,
    ...(command.keyboard && keybindings
      ? {
          keyboard: {
            ...omit(command.keyboard, [
              'keybindings_mac',
              'keybindings_pc',
            ] satisfies KeybindingsKey[]),
            // Normalize and order the key combinations to ensure that it is not
            // necessary to do anywhere else down the line.
            keybindings: normativeOrderKeyCombinations(
              normalizeKeyCombinations(keybindings),
            ),
          },
        }
      : {}),
  } as TCommandDefinition;
}
